import { Timestamp } from 'firebase/firestore';
import { Project } from '../types';

/**
 * Shared "what needs me" logic for the desktop lanes and the phone's
 * Idag / Denna vecka block, so the two surfaces can never disagree about
 * what is happening today.
 */
export type EventKind = 'pickup' | 'evaluation';

export const startOfDay = (d: Date): Date => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
export const sameDay = (a: Date | null, b: Date | null): boolean => !!a && !!b && startOfDay(a).getTime() === startOfDay(b).getTime();
export const dateOf = (t: Timestamp | null | undefined): Date | null => (t ? t.toDate() : null);

/** The date the team is steering toward right now. */
export const nextDate = (p: Project): { date: Date | null; kind: EventKind; time?: string } => {
  const pickup = dateOf(p.dates.scheduledPickup);
  const evaluation = dateOf(p.dates.evaluation);
  if ((p.status === 'värderad' || p.status === 'planerad') && pickup) return { date: pickup, kind: 'pickup', time: p.dates.scheduledPickupTime };
  if (evaluation) return { date: evaluation, kind: 'evaluation', time: p.dates.evaluationStartTime };
  return { date: pickup, kind: 'pickup', time: p.dates.scheduledPickupTime };
};

/** Why a project needs a person's attention, or null. */
export const needsAttention = (p: Project): string | null => {
  if (p.status === 'avslutad' || p.status === 'avbruten') return null;
  if (p.needsReview) return 'Kontrollera uppgifter';
  if (!p.contact?.name?.trim() || !p.contact?.phone?.trim()) return 'Kontaktuppgifter saknas';
  const pickup = dateOf(p.dates.scheduledPickup);
  const evaluation = dateOf(p.dates.evaluation);
  if (!pickup && evaluation && startOfDay(evaluation) < startOfDay(new Date())) return 'Hämtningsdatum saknas';
  if (p.status === 'värderad' && !pickup) return 'Hämtningsdatum saknas';
  // Pickup date passed but the project is still open: the week of grace
  // before the nightly job closes it. Say how long it has been.
  if (pickup && (p.status === 'planerad' || p.status === 'värderad')) {
    const days = Math.floor((startOfDay(new Date()).getTime() - startOfDay(pickup).getTime()) / 86400000);
    if (days >= 1) return days === 1 ? 'Hämtning igår, fortfarande öppen' : `Hämtning för ${days} dagar sedan, fortfarande öppen`;
  }
  return null;
};

export const isActive = (p: Project): boolean => p.status !== 'avslutad' && p.status !== 'avbruten';

export const weekBounds = (d: Date) => {
  const start = startOfDay(d);
  const dow = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dow);
  const end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
  return { start, end };
};

export interface Lanes {
  today: Project[];
  week: Project[];
  attention: { p: Project; why: string }[];
}

/** Today's events, the rest of this week's upcoming events, and open gaps. */
export const computeLanes = (projects: Project[], now: Date = new Date()): Lanes => {
  const { end } = weekBounds(now);
  const active = projects.filter(isActive);
  const todayStart = startOfDay(now);
  const byTime = (a: Project, b: Project) => (nextDate(a).time || '').localeCompare(nextDate(b).time || '');
  const today = active.filter(p => sameDay(nextDate(p).date, now)).sort(byTime);
  const week = active
    .filter(p => { const n = nextDate(p); return !!n.date && n.date > todayStart && n.date <= end && !sameDay(n.date, now); })
    .sort((a, b) => (nextDate(a).date!.getTime() - nextDate(b).date!.getTime()) || byTime(a, b));
  const attention = active.map(p => ({ p, why: needsAttention(p) })).filter(x => x.why) as { p: Project; why: string }[];
  return { today, week, attention };
};

/** Colour language shared by calendar, desktop lanes and the phone: blue = hembesök, green = hämtning. */
export const KIND_DOT: Record<EventKind, string> = { pickup: 'bg-emerald-500', evaluation: 'bg-blue-500' };
export const KIND_TEXT: Record<EventKind, string> = { pickup: 'text-emerald-800 dark:text-emerald-300', evaluation: 'text-blue-800 dark:text-blue-300' };
export const KIND_LABEL: Record<EventKind, string> = { pickup: 'Hämtning', evaluation: 'Hembesök' };
